import { useCallback, useEffect } from 'react';

import {
  COMMENTS_LOGGED_IN,
  COMMENTS_SRG_LOGIN_NOT_SUPPORTED,
  COMMENTS_TRIGGER_FIND_SESSION,
  COMMENTS_TRIGGER_LOGOUT,
} from '@default/webview/js/classes/SrfCommentsEvents';
import { triggerEvent } from '@fec/assets/js/utils/event';
import { useAppContext, useAppDispatcher } from '@Comments/context/AppContext';
import { Header } from '@Comments/components/Header/Header';
import { Content } from '@Comments/components/Content/Content';
import { useLoader } from '@Comments/services/openapiClient';
import { Spinner } from '@Comments/components/Spinner/Spinner';
import { Error } from '@Comments/components/Error/Error';
import { FrontendThreadState } from '@Comments/models/FrontendThread';

import './App.scss';

export const App = () => {
  const { appState, activeReplyFormAfterLogin } = useAppContext();
  const dispatch = useAppDispatcher();
  const loader = useLoader();

  const loadThreadInfo = useCallback(async () => {
    const result = await loader.getThreadInfo();
    dispatch({
      type: 'update-thread',
      data: {
        state: result.state as FrontendThreadState,
        authorUsername: result.authorUsername,
        welcomeMessage: result.welcomeMessage,
        goodbyeMessage: result.goodbyeMessage,
      },
    });
    dispatch({ type: 'update-app-state', data: 'ready' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleLogin = useCallback(
    (event: Event) => {
      const data = (event as CustomEvent).detail;
      dispatch({
        type: 'login-user',
        data: {
          userName: data.username ?? '',
          accessToken: data.accessToken ?? '',
          activeReplyForm: activeReplyFormAfterLogin,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const handleLogout = useCallback(() => {
    dispatch({ type: 'logout-user' });
  }, [dispatch]);

  const handleSrgLoginNotSupported = useCallback(() => {
    dispatch({ type: 'set-srg-login-supported', data: false });
  }, [dispatch]);

  useEffect(() => {
    const initial = async () => {
      try {
        await loadThreadInfo();
      } catch {
        dispatch({
          type: 'update-app-state',
          data: 'error',
        });
      }
    };
    initial();
  }, [dispatch, loadThreadInfo]);

  useEffect(() => {
    window.addEventListener(COMMENTS_LOGGED_IN, handleLogin);
    window.addEventListener(COMMENTS_TRIGGER_LOGOUT, handleLogout);
    window.addEventListener(
      COMMENTS_SRG_LOGIN_NOT_SUPPORTED,
      handleSrgLoginNotSupported,
    );
    triggerEvent(COMMENTS_TRIGGER_FIND_SESSION);
    return () => {
      window.removeEventListener(COMMENTS_LOGGED_IN, handleLogin);
      window.removeEventListener(COMMENTS_TRIGGER_LOGOUT, handleLogout);
      window.removeEventListener(
        COMMENTS_SRG_LOGIN_NOT_SUPPORTED,
        handleSrgLoginNotSupported,
      );
    };
  }, [
    activeReplyFormAfterLogin,
    handleLogin,
    handleLogout,
    handleSrgLoginNotSupported,
  ]);

  return (
    <>
      <Header />
      {appState === 'loading' && (
        <div className="srf-comments-spinner-container">
          <Spinner />
        </div>
      )}
      {appState === 'error' && <Error cssClass="srf-comments-app-error" />}
      {!['loading', 'error'].includes(appState) && <Content />}
    </>
  );
};
